import ThreeGL from './three';
import { isNumber, replaceAllByIndex } from './utils';

class Three3D {
    #element; // 渲染节点
    #three = new ThreeGL();
    #boxData = []; // 数据
    #animationFrameRunning = false; // 渲染状态
    // Group
    #boundaryGroup = this.#three.createGroup(); // 容器边界
    #palletsGroup = this.#three.createGroup(); // 箱子组
    #axisGroup = this.#three.createGroup(); // 坐标组
    #gridGroup = this.#three.createGroup(); // 网格
    // constant
    #defaultOptions = {
        size: { length: 10, width: 10, height: 10 },
        axis: { color: ['#0000ff', '#ff0000', '#00ff00'] },
        grid: { color: '#888', division: 10 },
        boundary: {
            color: ['#999c', '#aaa6'],
        },
    };
    #defaultStyle = {
        visible: true,
        borderColor: '#000000',
        coverColor: '#f3033c',
        openMidline: true,
    };
    constructor(dom = null, options = {}) {
        if (!dom) {
            console.error('dom is required!');
            return;
        }
        this.#element = dom;
        this.#three.definedScene();
        // 平行光设置
        this.#three.addDirectionalLight('#fff', 0.7, (that) => {
            that.position.set(20, 400, 600);
        });
        // 环境光
        this.#three.addAmbientLight('#e6e6fa');
        this.#three.definedRenderer({ antialias: true });
        // 添加到场景
        this.#three.scene.add(
            this.#palletsGroup,
            this.#axisGroup,
            this.#boundaryGroup,
            this.#gridGroup,
        );
        // 固件初始化
        this.setOptions({ size: this.#defaultOptions.size, ...options });
        this.#initThree();
    }
    #initThree = () => {
        const { length, width, height } = this.#defaultOptions.size;

        const clientWidth = this.#element.clientWidth; // 窗口宽度
        const clientHeight = this.#element.clientHeight; // 窗口高度
        // 相机初始化
        this.#three.definedCamera(8, clientWidth / clientHeight, 1, 100000);
        this.#three.camera.position.set(
            Math.max(length, width) * 6,
            Math.min(length, width) * 12,
            height * 6,
        );
        this.#three.camera.up.set(0, 0, 1); // 相机以哪个方向为上方
        this.#three.camera.lookAt(0, 0, 0); // 相机看向哪个坐标
        // 渲染器初始化
        this.#three.renderer.setPixelRatio(window.devicePixelRatio);
        this.#three.renderer.setSize(clientWidth, clientHeight); // 设置渲染区域尺寸
        this.#three.renderer.setClearColor('#fff', 1); // 设置背景颜色
        this.#element.appendChild(this.#three.renderer.domElement);
        // 添加控件对象
        this.#three.definedControls(this.#three.camera, this.#three.renderer.domElement);
        this.#three.controls.enableZoom = true;
        // this.#three.controls.zoomSpeed = 0.4;
        this.#three.controls.enablePan = true;
        // this.#three.controls.panSpeed = 0.2;
        this.#three.controls.enableRotate = true;
        // this.#three.controls.rotateSpeed = 0.2;
        this.render();
    };
    // 渲染
    #render = () => {
        if (this.#animationFrameRunning) {
            this.#three.renderer.render(this.#three.scene, this.#three.camera);
            window.requestAnimationFrame(this.#render);
        }
    };
    // 坐标系
    #setAxis = (option) => {
        const { color } = option;
        replaceAllByIndex(this.#defaultOptions.axis.color, color);
        this.#axisGroup.clear();
        const { length, width, height } = this.#defaultOptions.size;
        const size = (length + width + height) / 60; // 字体尺寸单位，大小决定刻度长短和数字尺寸
        this.#defaultOptions.axis.color.forEach((hex, name) => {
            if (name === 0) {
                this.#axisGroup.add(
                    this.#three.createAxis(0, { size, length: length, color: hex }),
                );
            }
            if (name === 1) {
                this.#axisGroup.add(this.#three.createAxis(1, { size, length: width, color: hex }));
            }
            if (name === 2) {
                this.#axisGroup.add(
                    this.#three.createAxis(2, { size, length: height, color: hex }),
                );
            }
        });
    };
    // 容器边界
    #setBoundary = (option) => {
        const { color } = option;
        replaceAllByIndex(this.#defaultOptions.boundary.color, color);
        this.#boundaryGroup.clear();
        const [borderColor, coverColor] = this.#defaultOptions.boundary.color;
        this.#boundaryGroup.add(
            ...this.#three.createBoundary({
                ...this.#defaultOptions.size,
                borderColor,
                coverColor,
            }),
        );
    };
    // 底面网格
    #setGrid = (option) => {
        const { color, division } = option;
        color && (this.#defaultOptions.grid.color = color);
        isNumber(division) && (this.#defaultOptions.grid.division = division);
        this.#gridGroup.clear();
        const { length, width, height } = this.#defaultOptions.size;
        this.#gridGroup.add(
            this.#three.createGridHelper(
                Math.max(length, width, height),
                this.#defaultOptions.grid.division,
                this.#defaultOptions.grid.color,
                (that) => {
                    that.position.set(length / 2, width / 2, 0);
                },
            ),
        );
    };
    /**
     * 添加单个箱子
     */
    #addData = ({ id, x, y, z, depth, width, height, customise = {} }) => {
        const _customise = { ...this.#defaultStyle, ...customise };
        const box = this.#three.createBox({
            name: id,
            width,
            height,
            length: depth,
            x: y,
            y: x,
            z,
            ..._customise,
        });
        this.#palletsGroup.add(box);
    };
    /**
     * 设置箱体数据
     * @param {object[] | ((data: object[]) => object[])} data 数据数组，或返回数据数组的函数
     */
    setData = (data) => {
        let _data = [];
        if (typeof data === 'function') {
            let back = data([...this.#boxData]);
            _data = Array.isArray(back) ? back : [];
        }
        if (Array.isArray(data)) {
            _data = data;
        }
        this.#boxData = _data;
        this.#palletsGroup.clear();
        _data.forEach((item) => this.#addData(item));
    };
    /**
     *
     */
    setOptions = (options) => {
        let _options = options ? { ...options } : {};
        if (Object.prototype.hasOwnProperty.call(options, 'size')) {
            const { length, width, height } = options.size || {};
            isNumber(length) && (this.#defaultOptions.size.length = length);
            isNumber(width) && (this.#defaultOptions.size.width = width);
            isNumber(height) && (this.#defaultOptions.size.height = height);
            this.#three.camera?.position.set(
                Math.max(length, width) * 12,
                Math.min(length, width) * 12,
                height * 12,
            );
            this.#three.camera?.up.set(0, 0, 1); // 相机以哪个方向为上方
            this.#three.camera?.lookAt(0, 0, 0); // 相机看向哪个坐标
            _options = { axis: {}, grid: {}, boundary: {}, ..._options };
        }
        if (Object.prototype.hasOwnProperty.call(_options, 'axis')) {
            const axis = _options.axis;
            if (axis) {
                this.#setAxis(axis);
            } else {
                this.#axisGroup.clear();
            }
        }
        if (Object.prototype.hasOwnProperty.call(_options, 'grid')) {
            const grid = _options.grid;
            if (grid) {
                this.#setGrid(grid);
            } else {
                this.#gridGroup.clear();
            }
        }
        if (Object.prototype.hasOwnProperty.call(_options, 'boundary')) {
            const boundary = _options.boundary;
            if (boundary) {
                this.#setBoundary(boundary);
            } else {
                this.#boundaryGroup.clear();
            }
        }
    };
    setSize = (width = this.#element.clientWidth, height = this.#element.clientHeight) => {
        this.#three.camera.aspect = width / height;
        this.#three.camera.updateProjectionMatrix();
        this.#three.renderer.setSize(width, height);
        this.#three.renderer.setPixelRatio(window.devicePixelRatio);
    };
    render = () => {
        if (
            this.#three.renderer &&
            this.#three.camera &&
            this.#three.scene &&
            !this.#animationFrameRunning
        ) {
            this.#animationFrameRunning = true;
            this.#render();
        }
    };
    // 卸载ThreeJs
    destroy = () => {
        if (this.#animationFrameRunning) {
            this.#animationFrameRunning = false;
            this.#three.clear();
            this.#element.removeChild(this.#three.renderer.domElement);
        }
    };
    // 获取帧图像
    shootCanvas = (x = 0, y = 0, z = 0) => {
        return this.#three.getCanvasElement(x, y, z);
    };
}

export default Three3D;
